import React, { useState, useRef, useEffect } from 'react';
import { COUNTRIES, resolveCountry, Country } from '../data/countries';

interface Props {
  value: string;
  onChange: (v: string) => void;
  lang?: string;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

// Αναζητήσιμο dropdown χωρών (ISO). value = English name (ταιριάζει με aade_nationality_aliases
// -> alpha3 στο AADE export). Δέχεται και παλιό ελεύθερο κείμενο: το εμφανίζει ως έχει.
export default function CountrySelect({ value, onChange, lang = 'en', placeholder = 'Nationality', disabled, className }: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [hi, setHi] = useState(0);
  const ref = useRef<HTMLDivElement | null>(null);

  const label = (c: Country) => (lang === 'el' && c.el ? c.el : c.en);
  const selected = resolveCountry(value);
  const display = selected ? label(selected) : (value || '');

  useEffect(() => {
    const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const q = query.trim().toLowerCase();
  const filtered = !q ? COUNTRIES : COUNTRIES.filter(c =>
    c.en.toLowerCase().includes(q) || (c.el || '').toLowerCase().includes(q) || c.iso2 === q || c.iso3.toLowerCase().includes(q));

  const pick = (c: Country) => { onChange(c.en); setOpen(false); setQuery(''); };

  return (
    <div ref={ref} className="relative">
      <input
        type="text"
        value={open ? query : display}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete="off"
        onFocus={() => { if (!disabled) { setOpen(true); setQuery(''); setHi(0); } }}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); setHi(0); }}
        onKeyDown={(e) => {
          if (!open) return;
          if (e.key === 'ArrowDown') { e.preventDefault(); setHi(h => Math.min(h + 1, filtered.length - 1)); }
          else if (e.key === 'ArrowUp') { e.preventDefault(); setHi(h => Math.max(h - 1, 0)); }
          else if (e.key === 'Enter') { e.preventDefault(); if (filtered[hi]) pick(filtered[hi]); }
          else if (e.key === 'Escape') { setOpen(false); }
        }}
        className={className}
      />
      {open && !disabled && (
        <div className="absolute z-[80] mt-1 w-full max-h-56 overflow-auto bg-white border-2 border-gray-300 rounded-lg shadow-lg">
          {filtered.length === 0 ? (
            <div className="px-3 py-2 text-sm text-gray-400">—</div>
          ) : filtered.map((c, i) => (
            <div key={c.iso3}
              onMouseEnter={() => setHi(i)}
              onMouseDown={(e) => { e.preventDefault(); pick(c); }}
              className={'px-3 py-2 text-sm cursor-pointer flex justify-between ' + (i === hi ? 'bg-blue-50' : '')}>
              <span>{label(c)}</span><span className="text-gray-400 text-xs">{c.iso2.toUpperCase()}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
